<script setup lang="ts">
import { useLayout } from "@/layout/common/composables/layout";
import { computed } from "vue";

const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const { themeMode, setThemeMode, calculatedMenuMode } = useLayout();

type ThemeOption = {
  value: "light" | "dark" | "auto";
  icon: string;
  label: string;
};

const themeOptions: ThemeOption[] = [
  { value: "light", icon: "pi pi-sun", label: "Light" },
  { value: "dark", icon: "pi pi-moon", label: "Dark" },
  { value: "auto", icon: "pi pi-desktop", label: "Auto" },
];

const selectedTheme = computed({
  get: () => themeMode.value,
  set: (value) => {
    if (!document.startViewTransition) {
      setThemeMode(value);
      return;
    }
    document.startViewTransition(() => {
      setThemeMode(value);
    });
  },
});

const isCompactMode = computed(() => {
  return (
    props.compact ||
    calculatedMenuMode.value === "slim" ||
    calculatedMenuMode.value === "compact"
  );
});
</script>

<template>
  <div v-if="!isCompactMode" class="flex flex-col ">
    <div
      class="relative flex items-center gap-0.5 p-0.5 rounded-lg"
    >
      <button
        v-for="option in themeOptions"
        :key="option.value"
        @click="selectedTheme = option.value"
        :class="[
          'relative z-10 flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md transition-all duration-300 ease-out',
          selectedTheme === option.value
            ? 'text-white shadow-md scale-[1.02]'
            : 'text-muted-color hover:bg-surface-200/50 dark:hover:bg-surface-800/50 hover:text-foreground hover:scale-[1.01]',
        ]"
        :title="option.label"
      >
        <div
          v-if="selectedTheme === option.value"
          class="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-md transition-all duration-300"
        />
        <i
          :class="[
            option.icon,
            'relative text-sm transition-transform duration-300',
            selectedTheme === option.value && 'scale-110',
          ]"
        />
        <span
          :class="[
            'relative text-[10px] font-semibold hidden lg:inline transition-all duration-300',
            selectedTheme === option.value && 'tracking-wide',
          ]"
        >
          {{ option.label }}
        </span>
      </button>
    </div>
  </div>
  <!-- Compact version for slim/compact modes -->
  <div v-else class="flex flex-col gap-1.5 items-center">
    <button
      v-for="option in themeOptions"
      :key="option.value"
      @click="selectedTheme = option.value"
      :class="[
        'relative w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300',
        selectedTheme === option.value
          ? 'text-white shadow-md scale-110'
          : 'text-muted-color hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-foreground hover:scale-105',
      ]"
      :title="option.label"
    >
      <div
        v-if="selectedTheme === option.value"
        class="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg"
      />
      <i
        :class="[
          option.icon,
          'relative text-base transition-transform duration-300',
          selectedTheme === option.value && 'scale-110',
        ]"
      />
    </button>
  </div>
</template>


